/**
 * Static snapshot fallback for the public API.
 *
 * Normal operation is UNCHANGED: every request goes to the live API. This code
 * only runs when a GET request FAILS (network error / 5xx, e.g. a backend 503).
 * In that case it serves an equivalent response reconstructed from the static
 * JSON snapshots in /public/data/ (built by scripts/generate-static-data.js),
 * so the public site keeps showing content instead of going blank.
 *
 * It replicates the backend's list filtering / sorting / pagination
 * (see backend/server/routes/public.js) so offline results match the live API.
 *
 * Writes (POST/PUT/DELETE) are never faked — they still fail when the API is
 * down, which is correct.
 */

// Cache fetched snapshot files for the session so we don't refetch per request.
const _fileCache = new Map();

async function loadSnapshot(file) {
  if (_fileCache.has(file)) return _fileCache.get(file);
  const promise = fetch(`/data/${file}`, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  _fileCache.set(file, promise);
  return promise;
}

function toArray(v) {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
}

/** Mirror of backend scholarship list: keyword/country/degree/funding + sort + paginate. */
function buildScholarshipList(all, params = {}) {
  const page = Math.max(1, parseInt(params.page, 10) || 1);
  const limit = Math.min(Math.max(1, parseInt(params.limit, 10) || 10), 100);

  const keyword = params.keyword ? String(params.keyword).trim().toLowerCase() : null;
  const country = params.country ? String(params.country).trim() : null;
  const degrees = toArray(params.degree).map((s) => String(s).trim()).filter(Boolean);
  const funding = toArray(params.funding).map((s) => String(s).trim()).filter(Boolean);

  let items = all.slice();

  if (keyword) {
    items = items.filter((s) => {
      const title = (s.title || '').toLowerCase();
      const host = (s.host_institution || '').toLowerCase();
      return title.includes(keyword) || host.includes(keyword);
    });
  }
  if (country) {
    items = items.filter((s) => s.country && s.country.name === country);
  }
  if (funding.length) {
    items = items.filter((s) => funding.includes(s.funding_status));
  }
  if (degrees.length) {
    items = items.filter((s) =>
      (s.eligible_degrees || []).some((d) => degrees.includes(d.name))
    );
  }

  const byNewest = (a, b) =>
    new Date(b.created_at) - new Date(a.created_at) || b.id - a.id;
  const byOldest = (a, b) =>
    new Date(a.created_at) - new Date(b.created_at) || a.id - b.id;
  const byTitleAsc = (a, b) => String(a.title).localeCompare(String(b.title)) || a.id - b.id;
  const byTitleDesc = (a, b) => String(b.title).localeCompare(String(a.title)) || a.id - b.id;
  const sorter = { oldest: byOldest, title_asc: byTitleAsc, title_desc: byTitleDesc, newest: byNewest }[
    params.sort
  ] || byNewest;
  items.sort(sorter);

  const count = items.length;
  const start = (page - 1) * limit;
  const results = items.slice(start, start + limit);
  return { count, results, page, total_pages: Math.max(1, Math.ceil(count / limit)) };
}

/** Mirror of backend blog list: optional search + category filter. */
function buildBlogList(all, params = {}) {
  let items = all.slice();
  const search = params.search ? String(params.search).trim().toLowerCase() : null;
  const category = params.category ? String(params.category).trim() : null;
  if (category) items = items.filter((b) => b.category === category);
  if (search) {
    items = items.filter((b) => (b.title || '').toLowerCase().includes(search));
  }
  return items;
}

/**
 * Resolve a failed request to snapshot data, or return null if we can't.
 * `url` is the axios request path relative to /api/public (e.g. "/scholarships/123/").
 */
export async function resolveFromSnapshot(url, params) {
  // Normalize: strip query string and leading/trailing slashes → segments
  const clean = String(url || '').split('?')[0];
  const seg = clean.split('/').filter(Boolean); // e.g. ["scholarships","123"]

  if (seg.length === 0) return null;
  const [root, id] = seg;

  switch (root) {
    case 'scholarships': {
      if (id) {
        return loadSnapshot(`scholarships/${id}.json`);
      }
      const all = await loadSnapshot('scholarships.json');
      if (!all) return null;
      return buildScholarshipList(all, params);
    }
    case 'blogs': {
      if (id) return loadSnapshot(`blogs/${id}.json`);
      const all = await loadSnapshot('blogs.json');
      if (!all) return null;
      return buildBlogList(all, params);
    }
    case 'scholarship-stories': {
      if (id) return loadSnapshot(`scholarship-stories/${id}.json`);
      return (await loadSnapshot('scholarship-stories.json')) || [];
    }
    case 'countries':
      return loadSnapshot('countries.json');
    case 'funding-statuses':
      return loadSnapshot('funding-statuses.json');
    case 'degree-levels':
      return loadSnapshot('degree-levels.json');
    case 'blog-categories':
      return loadSnapshot('blog-categories.json');
    default:
      return null;
  }
}

/**
 * Attach the fallback to an axios instance. On a failed GET, try the snapshot;
 * if found, resolve with a synthetic 200 response so callers work unchanged.
 */
export function attachStaticFallback(apiInstance) {
  apiInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error && error.config;
      const method = (config && config.method ? config.method : 'get').toLowerCase();
      // Only fall back for reads. Never fake writes.
      if (!config || method !== 'get') return Promise.reject(error);

      try {
        const data = await resolveFromSnapshot(config.url, config.params || {});
        if (data != null) {
          if (typeof console !== 'undefined') {
            console.warn('[static-fallback] API unreachable — serving cached snapshot for', config.url);
          }
          return {
            data,
            status: 200,
            statusText: 'OK (offline snapshot)',
            headers: {},
            config,
            request: null,
          };
        }
      } catch (e) {
        // fall through to original error
      }
      return Promise.reject(error);
    }
  );
}
